import type { SessionSummary, SessionsResponse } from '../../../../shared/types/sessions';
import type { ErrorResponse } from '../../../../shared/types/epics';
import { authFetch } from './authFetch';
import { API_BASE } from './version';

/**
 * Fetch today's agent sessions for the /today page.
 */
export async function fetchSessionsToday(): Promise<SessionSummary[]> {
  const response = await authFetch(`${API_BASE}/sessions/today`, { method: 'GET' });

  if (!response.ok) {
    let message = `Failed to fetch sessions: ${response.statusText}`;
    try {
      const body = (await response.json()) as ErrorResponse;
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }

  const body = (await response.json()) as SessionsResponse;
  return body.data.sessions;
}

export interface LogEvent {
  timestamp: number;
  message: string;
  streamName: string;
}

export interface SessionLogsResponse {
  events: LogEvent[];
  nextToken: string | null;
  logGroup: string;
  taskId: string;
  streamPrefix?: string;
  warning?: string;
}

/**
 * Fetch CloudWatch log events for a given ECS task.
 * Backend calls `FilterLogEvents` scoped to the task's log stream.
 *
 * URL hygiene: `task` is the ECS task ID (last ARN segment) or a local-*
 * marker. Full ARNs are accepted and reduced to the ID here — request URLs
 * must never carry the AWS account id / region / cluster embedded in an ARN.
 */
export async function fetchSessionLogs(params: {
  task: string;
  startTime?: string;
  endTime?: string;
  limit?: number;
  nextToken?: string;
}): Promise<SessionLogsResponse> {
  const taskId = params.task.includes('/') ? (params.task.split('/').pop() as string) : params.task;
  const qs = new URLSearchParams({ task: taskId });
  if (params.startTime) qs.set('startTime', params.startTime);
  if (params.endTime)   qs.set('endTime',   params.endTime);
  if (params.limit)     qs.set('limit',     String(params.limit));
  if (params.nextToken) qs.set('nextToken', params.nextToken);

  const response = await authFetch(`${API_BASE}/sessions/logs?${qs}`, { method: 'GET' });
  if (!response.ok) {
    let message = `Failed to fetch logs: ${response.statusText}`;
    try {
      const body = (await response.json()) as ErrorResponse;
      if (body?.error?.message) message = body.error.message;
    } catch { /* not JSON */ }
    throw new Error(message);
  }
  const body = (await response.json()) as { data: SessionLogsResponse };
  return body.data;
}
